import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { UIElement } from '../ui-element';
import type { UIOptionData, UIOptionMethods } from './ui-option.types';
import type { Attributes } from '../ui-element.type';
import type { UndefinedOr } from '../../global/utils.type';
import styles from './ui-option.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOption
 * @classdesc Abstract base class for all ui-option-* elements.
 * Defines common attributes shared across option components.
 * @property {boolean} [optgroup] {@attr optgroup} Marks this option as a group header.
 * @property {boolean} [control] {@attr control} Flag to distinguish the location where option will be shown (control button vs popover list).
 * @property {boolean} [selectable] {@attr selectable} Reserves space for a checkmark indicator. Set when the list supports item selection.
 * @property {boolean} [selected] {@attr selected} Marks this option as the currently selected item.
 * @property {boolean} [disabled] {@attr disabled} Marks this option as disabled.
 * @property {string} [text] {@attr text} Text label for the option.
 * @property {string} [value] {@attr value} Value of the option, used as fallback text when text attribute is not set.
 */
@Component({
    tag: 'ui-option',
    styles: styles,
})
export class UIOption extends UIElement implements UIOptionMethods {
    // eslint-disable-next-line local/immutable-constant-naming -- not an immutable constant but an overridden property
    static readonly PopoverType =
        'basic'; /* backward compatibility: no need for current solution */
    // eslint-disable-next-line local/immutable-constant-naming -- not an immutable constant but an overridden property
    static readonly HandlesOwnControl: boolean = false;

    @Attribute(Boolean) optgroup?: boolean;
    @Attribute(Boolean) control?: boolean;
    @Attribute(Boolean) selectable?: boolean;
    @Attribute(Boolean) selected?: boolean;
    @Attribute(Boolean) disabled?: boolean;
    @Attribute(String) text?: string;
    @Attribute(String) value?: string;

    @Child('.ui-option__checkmark') checkmark?: HTMLElement;
    @Child('.ui-option__caret') caret?: HTMLElement;

    getText?(): UndefinedOr<string>;
    getData?(): Attributes | UIOptionData;

    static get observedAttributes(): Array<string> {
        return ['selected', 'selectable', 'text'];
    }

    /**
     * Builds the label for an optgroup option.
     * @returns {Text}
     */
    buildOptGroupLabel(): Text {
        return document.createTextNode(this.getAttribute('text') ?? '');
    }

    /**
     * Updates the checkmark element based on the current state of selectable and selected attributes.
     * @private
     */
    private updateCheckmark(): void {
        if (this.selectable && this.selected) {
            if (!this.checkmark) {
                this.insertElements([
                    {
                        tagName: 'ui-icon',
                        classList: { 'ui-option__checkmark': true },
                        attributes: {
                            glyph: 'redesign-check',
                            color: 'bark-70',
                        },
                    },
                ]);
            }
        } else {
            this.checkmark?.remove();
        }
    }

    /**
     * Updates the caret element based on the current state of control attribute.
     * @private
     */
    private updateCaretSymbol(): void {
        if (this.control) {
            if (!this.caret) {
                this.insertElements([
                    {
                        tagName: 'ui-icon',
                        classList: { 'ui-option__caret': true },
                        attributes: {
                            glyph: 'redesign-caret-down',
                        },
                    },
                ]);
            }
        } else {
            this.caret?.remove();
        }
    }

    /**
     * Updates the painted option text when the host `text` attribute changes.
     * @param {string | null} newValue
     * @private
     */
    private updateText(newValue: string | null): void {
        if (this.optgroup) {
            const textNode = [...this.childNodes].find(
                (node) => node.nodeType === Node.TEXT_NODE
            );
            if (textNode) {
                textNode.textContent = newValue ?? '';
            }
            return;
        }
        const label = this.querySelector('.ui-option__label');
        if (label) {
            label.textContent = newValue || this.getAttribute('value') || '';
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        _oldValue: string | null,
        newValue: string | null
    ): void {
        if (!this.hydrated) {
            return;
        }
        if (name === 'selected' || name === 'selectable') {
            this.updateCheckmark();
        }
        if (name === 'text') {
            this.updateText(newValue);
        }
    }

    /**
     * @inheritDoc
     */
    render(): void {
        // To be used in subclasses for rendering specific content, while keeping checkmark and caret logic consistent across all options.
        this.updateClassList({
            '-control': !!this.control,
        });
        this.updateCheckmark();
        if (!(this.constructor as typeof UIOption).HandlesOwnControl) {
            this.updateCaretSymbol();
        }
    }
}
